import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Bike, ClipboardCheck, LogIn, CheckCircle2, Users, Car, ArrowRight, Truck } from "lucide-react";
import { numTrabajo, fmtFecha } from "@/pages/TrabajosActivos";

const ESTADO_LABEL = { pendiente: "Pendiente", en_proceso: "En proceso", activo: "Activo" };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const { user, tienePermiso } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/resumen").then((r) => setData(r.data)).finally(() => setCargando(false));
  }, []);

  const tarjetas = [
    { key: "motos_taller", label: "Motos en taller", icon: Bike, color: "indigo", to: "/trabajos/activos", perm: "trabajos.ver" },
    { key: "pendientes_aprobacion", label: "Pendientes de aprobación", icon: ClipboardCheck, color: "amber", to: "/trabajos/activos", perm: "trabajos.ver" },
    { key: "ingresados_hoy", label: "Ingresados hoy", icon: LogIn, color: "sky", to: "/trabajos/activos", perm: "trabajos.ver" },
    { key: "finalizados_hoy", label: "Finalizados hoy", icon: CheckCircle2, color: "emerald", to: "/reportes/realizados", perm: "reportes.ver" },
    { key: "pedidos_atrasados", label: "Pedidos especiales atrasados", icon: Truck, color: "rose", to: "/pedidos-especiales?atrasados=1", perm: "pedidos.ver" },
  ];
  const COLORS = {
    indigo: "bg-indigo-50 text-indigo-600 border-indigo-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    sky: "bg-sky-50 text-sky-600 border-sky-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    rose: "bg-rose-50 text-rose-600 border-rose-100",
  };

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Hola, {user?.usuario} 👋</h1>
        <p className="text-sm text-slate-500">Resumen del taller de hoy</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {tarjetas.map((t) => {
          const Icon = t.icon;
          const clickable = tienePermiso(t.perm);
          return (
            <button key={t.key} data-testid={`dashboard-card-${t.key}`}
              onClick={() => clickable && navigate(t.to)}
              className={`text-left rounded-2xl border bg-white p-5 shadow-sm transition-all ${clickable ? "hover:shadow-md hover:-translate-y-0.5 cursor-pointer" : "cursor-default"}`}>
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center border ${COLORS[t.color]}`}>
                <Icon size={20} />
              </div>
              <p className="mt-3 text-3xl font-bold text-slate-800 tabular-nums" data-testid={`dashboard-valor-${t.key}`}>
                {cargando ? "—" : (data?.[t.key] ?? 0)}
              </p>
              <p className="text-sm text-slate-500">{t.label}</p>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border bg-white shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-slate-800">Motos actualmente en taller</h2>
            {tienePermiso("trabajos.ver") && (
              <button data-testid="dashboard-ver-activos" onClick={() => navigate("/trabajos/activos")}
                className="text-xs font-semibold text-indigo-600 hover:underline flex items-center gap-1">
                Ver todos <ArrowRight size={13} />
              </button>
            )}
          </div>
          {cargando && <p className="text-sm text-slate-400 py-6 text-center">Cargando…</p>}
          {!cargando && (data?.recientes || []).length === 0 && (
            <p data-testid="dashboard-sin-activos" className="text-sm text-slate-400 py-6 text-center">No hay motos en taller.</p>
          )}
          <div className="divide-y divide-slate-100">
            {(data?.recientes || []).map((t) => (
              <div key={t.id} data-testid={`dashboard-trabajo-${t.id}`} className="flex items-center gap-3 py-2.5">
                <span className="font-mono text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">{numTrabajo(t.numero)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">
                    {t.vehiculo?.placa || "—"} · {t.cliente?.nombre || "Cliente"}
                  </p>
                  <p className="text-xs text-slate-400">Ingresó {fmtFecha(t.fecha_ingreso)}</p>
                </div>
                <span className="text-[11px] font-semibold uppercase text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                  {ESTADO_LABEL[t.estado_operativo] || "Activo"}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border bg-white shadow-sm p-5">
          <h2 className="text-base font-semibold text-slate-800 mb-3">Registros</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-500"><Users size={18} /></div>
              <div><p className="text-xl font-bold text-slate-800 tabular-nums">{cargando ? "—" : data?.total_clientes ?? 0}</p><p className="text-xs text-slate-500">Clientes</p></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-500"><Car size={18} /></div>
              <div><p className="text-xl font-bold text-slate-800 tabular-nums">{cargando ? "—" : data?.total_vehiculos ?? 0}</p><p className="text-xs text-slate-500">Vehículos</p></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
